import React, { useState } from 'react';
import {
  Box, Typography, Divider, Card, CardHeader, CardContent,
  Grid, List, ListItem, ListItemIcon, ListItemText, Avatar,
  IconButton, Button, Dialog, DialogContent, DialogTitle
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  Today as TodayIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  AccountCircle as AccountCircleIcon,
  Work as WorkIcon,
  Info as InfoIcon,
  Category as CategoryIcon,
  Subject as SubjectIcon,
  Note as NoteIcon,
  PrintSharp, Edit, Link as LinkIcon
} from '@mui/icons-material';
import { lightBlue, teal, deepPurple, orange } from '@mui/material/colors';
import JMNotice from './LetterPrint';
import EditForm from './Editform';

const ViewFormModel = ({ formData, onClose }) => {
  const [selectedForm, setSelectedForm] = useState(formData || null);
  const [open, setOpen] = useState(null);
  const [mode, setMode] = useState(null);

  const sectionColors = {
    basic: lightBlue[50],
    applicant: teal[50],
    management: deepPurple[50],
    case: orange[50]
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Open':
        return <CheckCircleIcon color="success" />;
      case 'Closed':
        return <CancelIcon color="error" />;
      case 'Forwarded':
        return <AssignmentIcon color="warning" />;
      default:
        return <AssignmentIcon color="info" />;
    }
  };

  const handlePrint = (form) => {
    setSelectedForm(form);
    setMode('print');
    setOpen(true);
  };

  const handleEdit = (form) => {
    setSelectedForm(form);
    setMode('edit');
    setOpen(true);
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '85%',
    maxWidth: 900,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 0,
    borderRadius: 3,
    maxHeight: '90vh',
    overflowY: 'auto',
    border: '1px solid',
    borderColor: 'divider'
  };

  return (
    <>
      {selectedForm && (
        <Box sx={modalStyle}>
          <Box sx={{
            p: 3,
            background: 'linear-gradient(to right, #1976d2, #2196f3)',
            color: 'white'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Form Details
              </Typography>
              <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              File Number: {selectedForm.fileNumber}
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>

              {/* Basic Info */}
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ backgroundColor: sectionColors.basic }}>
                  <CardHeader
                    avatar={<InfoIcon color="primary" />}
                    title="Basic Information"
                    titleTypographyProps={{ variant: 'h6', color: 'primary' }}
                  />
                  <CardContent>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><AssignmentIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="File Number" secondary={selectedForm.fileNumber} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><TodayIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Received From" secondary={selectedForm.receiveFrom || 'Not provided'} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><TodayIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Received Date" secondary={selectedForm.receivedDate ? new Date(selectedForm.receivedDate).toLocaleDateString('en-GB', {
                            year: '2-digit', month: '2-digit', day: '2-digit'
                          }) : 'Not provided'} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CalendarTodayIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Next Hearing Date" secondary={selectedForm.nextDateOfHearing ? new Date(selectedForm.nextDateOfHearing).toLocaleDateString('en-GB', {
                            year: '2-digit', month: '2-digit', day: '2-digit'
                          }) : 'Not provided'} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>{getStatusIcon(selectedForm.finalStatus)}</ListItemIcon>
                        <ListItemText primary="Status" secondary={selectedForm.finalStatus || 'N/A'} />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Applicant Info */}
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ backgroundColor: sectionColors.applicant }}>
                  <CardHeader
                    avatar={<PersonIcon color="primary" />}
                    title="Applicant Details"
                    titleTypographyProps={{ variant: 'h6', color: 'primary' }}
                  />
                  <CardContent>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><AccountCircleIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Name" secondary={selectedForm.applicantName || 'Not provided'} />
                      </ListItem>

                    {selectedForm.applicantPhoneNumber?.length ? (
                    <ListItem alignItems="flex-start">
                        <ListItemIcon><PhoneIcon color="primary" /></ListItemIcon>
                        <ListItemText
                        primary="Phone Numbers"
                        secondary={
                            <Box>
                            {selectedForm.applicantPhoneNumber.map((phone, idx) => (
                                <Typography key={idx} variant="body2">
                                {idx + 1}. {phone.name} – <a href={`tel:${phone.number}`} style={{ color: 'inherit', textDecoration: 'underline' }}>{phone.number}</a>
                                </Typography>
                            ))}
                            </Box>
                        }
                        />
                    </ListItem>
                    ) : (
                    <ListItem>
                        <ListItemIcon><PhoneIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Phone" secondary="Not provided" />
                    </ListItem>
                    )}

                    <ListItem>
                    <ListItemIcon><EmailIcon color="primary" /></ListItemIcon>
                    <ListItemText
                        primary="Email"
                        secondary={
                        selectedForm.applicantMail ? (
                            <a href={`mailto:${selectedForm.applicantMail}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                            {selectedForm.applicantMail}
                            </a>
                        ) : 'Not provided'
                        }
                    />
                    </ListItem>

                      <ListItem>
                        <ListItemIcon><HomeIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Address" secondary={selectedForm.applicantAddress || 'Not provided'} />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Management Info */}
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ backgroundColor: sectionColors.management }}>
                  <CardHeader
                    avatar={<WorkIcon color="primary" />}
                    title="Management Details"
                    titleTypographyProps={{ variant: 'h6', color: 'primary' }}
                  />
                  <CardContent>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><AccountCircleIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Name" secondary={selectedForm.managementName || 'Not provided'} />
                      </ListItem>

                     {/* Management Phone Numbers (Formatted) */}
               {selectedForm.managementPhoneNumber?.length ? (
                    <ListItem alignItems="flex-start">
                        <ListItemIcon><PhoneIcon color="primary" /></ListItemIcon>
                        <ListItemText
                        primary="Phone Numbers"
                        secondary={
                            <Box>
                            {selectedForm.managementPhoneNumber.map((phone, idx) => (
                                <Typography key={idx} variant="body2">
                                {idx + 1}. {phone.name} – <a href={`tel:${phone.number}`} style={{ color: 'inherit', textDecoration: 'underline' }}>{phone.number}</a>
                                </Typography>
                            ))}
                            </Box>
                        }
                        />
                    </ListItem>
                    ) : (
                    <ListItem>
                        <ListItemIcon><PhoneIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Phone" secondary="Not provided" />
                    </ListItem>
                    )}

                    <ListItem>
                    <ListItemIcon><EmailIcon color="primary" /></ListItemIcon>
                    <ListItemText
                        primary="Email"
                        secondary={
                        selectedForm.managementMail ? (
                            <a href={`mailto:${selectedForm.managementMail}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                            {selectedForm.managementMail}
                            </a>
                        ) : 'Not provided'
                        }
                    />
                    </ListItem>

                      <ListItem>
                        <ListItemIcon><HomeIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Address" secondary={selectedForm.managementAddress || 'Not provided'} />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Case Info */}
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ backgroundColor: sectionColors.case }}>
                  <CardHeader
                    avatar={<CategoryIcon color="primary" />}
                    title="Case Information"
                    titleTypographyProps={{ variant: 'h6', color: 'primary' }}
                  />
                  <CardContent>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><SubjectIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Subject" secondary={selectedForm.subject || 'Not provided'} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CategoryIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Section" secondary={selectedForm.section || 'Not provided'} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><NoteIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Memo Number" secondary={selectedForm.memoNumber || 'Not provided'} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><NoteIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="Amount Recovered" secondary={selectedForm.amount || 'Not provided'} />
                      </ListItem>
                      {selectedForm.url && (
                        <ListItem>
                          <ListItemIcon><LinkIcon color="primary" /></ListItemIcon>
                          <ListItemText
                            primary="URL"
                            secondary={<a href={selectedForm.url} target="_blank" rel="noopener noreferrer">{selectedForm.url}</a>}
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Remarks */}
              {selectedForm.remarks && selectedForm.remarks.some(r => r.text) && (
                <Grid item xs={12}>
                  <Card elevation={0}>
                    <CardHeader
                      avatar={<NoteIcon color="primary" />}
                      title="Remarks History"
                      titleTypographyProps={{ variant: 'h6', color: 'primary' }}
                    />
                    <CardContent>
                      <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                        {selectedForm.remarks.map((remark, index) => (
                          remark.text && (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>{index + 1}</Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary={remark.text}
                                secondary={remark.date ? new Date(remark.date).toLocaleDateString('en-GB', {
                                  year: '2-digit', month: '2-digit', day: '2-digit'
                                }) : 'N/A'}
                                primaryTypographyProps={{ sx: { fontWeight: 500 } }}
                              />
                            </ListItem>
                          )
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => handleEdit(selectedForm)}
              startIcon={<Edit />}
              sx={{
                borderRadius: 2, mr: 2, textTransform: 'none', fontFamily: 'poppins',
                backgroundColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.dark' }
              }}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              onClick={() => handlePrint(selectedForm)}
              startIcon={<PrintSharp />}
              sx={{
                borderRadius: 2, mr: 2, textTransform: 'none', fontFamily: 'poppins',
                backgroundColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.dark' }
              }}
            >
              Print
            </Button>
            <Button
              variant="contained"
              onClick={handleClose}
              startIcon={<CloseIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontFamily: 'poppins' }}
            >
              Close Details
            </Button>
          </Box>
        </Box>
      )}

      {/* Edit Dialog */}
                <Dialog
            open={open && mode === 'edit'}
            onClose={handleClose}
            maxWidth="lg"     // increase max width to 'large'
            fullWidth         // make dialog take full width of maxWidth
            >
            <DialogContent>
                <EditForm formData={selectedForm} />
            </DialogContent>
            </Dialog>


      {/* Print Dialog */}
      <Dialog
        open={open && mode === 'print'}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
      >
        <DialogContent>
          <JMNotice formData={selectedForm} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ViewFormModel;
